import { borgoCountryName, isBorgoCountry, type ValidationRuleObject } from '@staff/shared'

import type { CitizenshipEntry, CountryIdListVariant, TaxDomicileEntry } from './CountryIdList.types'

/** One picked country with its ID number, whichever list it belongs to. */
export interface CountryIdRow {
  countryCode: string
  idNumber: string
  isPrimary: boolean
}

export const MIN_COUNTRIES = 1
export const MAX_COUNTRIES = 5
export const DEFAULT_COUNTRY = 'SE'

export const TOO_MANY_COUNTRIES_MESSAGE = `Högst ${MAX_COUNTRIES} länder kan väljas. Ta bort ett land först.`
export const LAST_COUNTRY_MESSAGE = 'Minst ett land måste vara valt.'
export const fixedCountryMessage = (countryCode: string) => `${borgoCountryName(countryCode)} kan inte tas bort.`

export const defaultCountryIdRows = (): CountryIdRow[] => [
  { countryCode: DEFAULT_COUNTRY, idNumber: '', isPrimary: true },
]

export const toCountryIdRows = (
  variant: CountryIdListVariant,
  entries: readonly (CitizenshipEntry | TaxDomicileEntry)[],
): CountryIdRow[] =>
  variant === 'citizenship'
    ? (entries as CitizenshipEntry[]).map((entry, index) => ({
        countryCode: entry.countryCode,
        idNumber: entry.registrationId,
        isPrimary: index === 0,
      }))
    : (entries as TaxDomicileEntry[]).map((entry) => ({
        countryCode: entry.countryCode,
        idNumber: entry.tin,
        isPrimary: entry.isPrimary,
      }))

/** A citizenship list has no primary flag: the primary goes first. */
export const fromCountryIdRows = (
  variant: CountryIdListVariant,
  rows: readonly CountryIdRow[],
): CitizenshipEntry[] | TaxDomicileEntry[] =>
  variant === 'citizenship'
    ? primaryFirst(rows).map((row) => ({
        countryCode: row.countryCode,
        registrationId: row.idNumber,
      }))
    : rows.map((row) => ({ countryCode: row.countryCode, tin: row.idNumber, isPrimary: row.isPrimary }))

/** The primary first, the others in their order: how a list shows when it opens. */
export const primaryFirst = (rows: readonly CountryIdRow[]): CountryIdRow[] => [
  ...rows.filter((row) => row.isPrimary),
  ...rows.filter((row) => !row.isPrimary),
]

/** The rows in the order they show on screen, so picking another primary moves no row. Rows not in `order` go last. */
export const inDisplayOrder = (rows: readonly CountryIdRow[], order: readonly string[]): CountryIdRow[] => {
  const position = (row: CountryIdRow) => {
    const index = order.indexOf(row.countryCode)
    return index === -1 ? order.length : index
  }
  return [...rows].sort((a, b) => position(a) - position(b))
}

export type CountryIdRowsChange = { rows: CountryIdRow[]; refused?: undefined } | { rows?: undefined; refused: string }

export const addCountry = (rows: readonly CountryIdRow[], countryCode: string): CountryIdRowsChange => {
  if (rows.some((row) => row.countryCode === countryCode)) return { rows: [...rows] }
  if (rows.length >= MAX_COUNTRIES) return { refused: TOO_MANY_COUNTRIES_MESSAGE }
  return { rows: [...rows, { countryCode, idNumber: '', isPrimary: rows.length === 0 }] }
}

/** Removing the primary makes the next remaining country primary. A fixed country stays. */
export const removeCountry = (
  rows: readonly CountryIdRow[],
  countryCode: string,
  fixedCountries: readonly string[] = [],
): CountryIdRowsChange => {
  if (fixedCountries.includes(countryCode)) return { refused: fixedCountryMessage(countryCode) }
  if (rows.length <= MIN_COUNTRIES) return { refused: LAST_COUNTRY_MESSAGE }
  const remaining = rows.filter((row) => row.countryCode !== countryCode)
  if (remaining.some((row) => row.isPrimary)) return { rows: remaining }
  return { rows: remaining.map((row, index) => ({ ...row, isPrimary: index === 0 })) }
}

export const setPrimaryCountry = (rows: readonly CountryIdRow[], countryCode: string): CountryIdRow[] =>
  rows.map((row) => ({ ...row, isPrimary: row.countryCode === countryCode }))

/** A fixed country's ID number is the caller's to set, so it stays as it is. */
export const setCountryIdNumber = (
  rows: readonly CountryIdRow[],
  countryCode: string,
  idNumber: string,
  fixedCountries: readonly string[] = [],
): CountryIdRow[] =>
  fixedCountries.includes(countryCode)
    ? [...rows]
    : rows.map((row) => (row.countryCode === countryCode ? { ...row, idNumber } : row))

/** The list's own rules, as the server applies them. The ID numbers are checked per row by their inputs. */
export const validateCountryIdRows = (rows: readonly CountryIdRow[]): string | true => {
  if (rows.length < MIN_COUNTRIES) return 'Välj minst ett land.'
  if (rows.length > MAX_COUNTRIES) return `Välj högst ${MAX_COUNTRIES} länder.`
  if (new Set(rows.map((row) => row.countryCode)).size !== rows.length) return 'Samma land är valt två gånger.'
  if (rows.some((row) => !isBorgoCountry(row.countryCode)))
    return 'Ett av länderna kan inte väljas. Välj ett land i listan.'
  if (rows.filter((row) => row.isPrimary).length !== 1) return 'Välj exakt ett primärt land.'
  return true
}

/** Every ID number is mandatory: there is no "not issued" option. */
export const idNumberRule = (idLabel: string, countryCode: string): ValidationRuleObject => ({
  id: `countryIdNumber(${idLabel}, ${countryCode})`,
  rule: (value: unknown) =>
    String(value ?? '').trim() !== '' || `${idLabel} måste fyllas i för ${borgoCountryName(countryCode)}.`,
})
